// Inter-entity transfer — move value between DubaiOTC and IndiaOTC desks.
//
// Rare flow (a few times per quarter). Reuses /api/transfers under the hood;
// the screen just wraps a source-entity / destination-entity selection around
// the standard rail pickers.
//
// Typical case: IndiaOTC desk receives a wire that's actually for DubaiOTC
// inventory replenishment — operator records it as a transfer from
// (indiaotc bank rail) → (dubaiotc treasury) in USDT-equivalent.
//
// Phone pass, 19 Sept 2026 (presentation only — the posting is untouched):
// the hand-rolled title + two-sentence paragraph became the shared
// ScreenHeader with the one-line subtitle, and the "asset must match" rule
// moved into About this screen (§6: no explanatory sentence on screen); the
// private Field copy gave way to components/Field (§7 rule 15); the amount
// box carries its asset beside it and says "Amount in USDT" once the From
// account is picked, as Move money does; Notes sits under the "More" fold
// (§7 rule 8); every red line is the one ErrorLine; the balance figure is
// an .amt.

import { useState, useMemo } from 'react';
import { errorWords, isSentinel } from '../lib/errors';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { tg } from '../lib/telegram';
import { ScreenHeader } from '../components/ScreenHeader';
import { Field } from '../components/Field';
import { ErrorLine } from '../components/ErrorLine';
import { MoreDetails } from '../components/MoreDetails';
import { RailPicker, type RailValue } from '../components/RailPicker';
import { RepeatPanel, useRepeatGuard } from '../components/RepeatPanel';
import { amountProblem } from '../lib/amount';
import { treasuryPath, makeReference } from '../lib/workflow';
import { displayAsset, fmtAmount } from '../lib/format';
import { railName, sameAsset } from '../lib/naming';

type Entity = 'dubaiotc' | 'indiaotc';

export function InterEntityTx() {
  const nav = useNavigate();
  const [srcEntity, setSrcEntity] = useState<Entity>('dubaiotc');
  const [dstEntity, setDstEntity] = useState<Entity>('indiaotc');
  const [src, setSrc] = useState<RailValue>({ slug: '', asset: '' });
  const [dst, setDst] = useState<RailValue>({ slug: '', asset: '' });
  const [amount, setAmount] = useState('');
  const [notes, setNotes] = useState('');
  // What the last posting moved, for the success line (§6 "Success line":
  // verb, amount, from → to, reference). Snapshotted at success, because the
  // figures clear on success (§7 rule 12) and the line must still read.
  const [posted, setPosted] = useState<{ amount: number; asset: string; from: string; to: string; reference: string } | null>(null);

  // Load each entity's treasury balances independently
  const srcTreasury = useQuery({
    queryKey: ['treasury', 'balances', srcEntity],
    queryFn:  () => api.treasuryBalances(srcEntity),
  });
  const dstTreasury = useQuery({
    queryKey: ['treasury', 'balances', dstEntity],
    queryFn:  () => api.treasuryBalances(dstEntity),
  });

  const srcRows = srcTreasury.data?.rows ?? [];
  const dstRows = dstTreasury.data?.rows ?? [];

  const srcRow = useMemo(
    () => srcRows.find((r) => r.slug === src.slug && sameAsset(r.asset, src.asset)),
    [srcRows, src.slug, src.asset],
  );
  const srcBalance = srcRow?.balance ?? 0;

  const amtN = Number(amount) || 0;
  // A running account (the angadiya) may go below zero — the books take it (14 Oct 2026).
  const overdraw = amtN > srcBalance && !srcRow?.running_account;

  const sameEntity = srcEntity === dstEntity;
  const assetMismatch = src.asset && dst.asset && !sameAsset(src.asset, dst.asset);
  const amountWords = amountProblem(amount);
  // The asset line used to end "for cross-asset, use Chain Swap inside one
  // entity then re-transfer". CHAIN SWAP IS RETIRED: books-v3 has no such
  // instrument, the route answers chain_swap_retired, and /treasury/chain-swap
  // opens Move money — so that sentence sent the operator to a door that does
  // not open. Name what is actually needed instead.
  const validation =
    sameEntity     ? 'Source and destination entity must differ — use Move money for same-entity transfers.' :
    amountWords    ? amountWords :
    !src.slug || !src.asset ? null :
    assetMismatch  ? `Both legs must be the same asset — pick an account on each side that holds it (got ${displayAsset(src.asset)} → ${displayAsset(dst.asset)}).` :
    overdraw       ? `Insufficient balance — source holds ${fmtAmount(srcBalance, src.asset)} ${displayAsset(src.asset)}.` :
    null;

  // Inter-entity meets the same repeat guard as every other transfer — the
  // panel and the confirm are shared (components/RepeatPanel.tsx).
  const { twin: repeat, clearTwin, guard } = useRepeatGuard();
  const submit = useMutation({
    // ONE ACCOUNT-PATH RULE FOR THE WHOLE CONSOLE. This screen kept a private
    // copy of the asset table, and it had already drifted: it knew none of the
    // v3 codes the rail rows actually carry, so a USDT wallet built
    // `treasury:…:crypto:hot-trc:unknown:usdt`, and it lacked the guard that
    // stops a `crypto.*` slug repeating its own category. The dialect layer
    // rescued the posting, but the raw path is what admin.audit_log keeps.
    // treasuryPath() takes the entity for exactly this screen.
    mutationFn: async (opts?: { confirmRepeat?: boolean }) => {
      const reference = makeReference('IETX');
      const r = await guard(() => api.transfer({
        source:      treasuryPath(src.slug, src.asset, srcEntity),
        destination: treasuryPath(dst.slug, dst.asset || src.asset, dstEntity),
        amount:    { asset: src.asset, amount: amtN },
        fee_payer: 'house',
        reference,
        notes: notes || `Inter-entity ${srcEntity} → ${dstEntity}`,
        confirm_repeat: opts?.confirmRepeat || undefined,
      }));
      return { ...r, reference: r.reference ?? reference };
    },
    onSuccess: (r) => {
      clearTwin();
      tg()?.HapticFeedback.notificationOccurred('success');
      const nameOf = (v: RailValue, rows: typeof srcRows) =>
        railName(rows.find((x) => x.slug === v.slug && sameAsset(x.asset, v.asset)) ?? { slug: v.slug });
      setPosted({ amount: amtN, asset: src.asset, from: nameOf(src, srcRows), to: nameOf(dst, dstRows), reference: r.reference });
      setAmount(''); setNotes('');
    },
    onError:   (e) => { if (!isSentinel(e)) tg()?.HapticFeedback.notificationOccurred('error'); },
  });

  return (
    <div className="space-y-3">
      <button onClick={() => nav(-1)} className="btn-link text-meta">← Back</button>
      {/* The house header: title, the one-line subtitle, and the rule that
          used to be a second sentence on screen now under About. */}
      <ScreenHeader
        title="Inter-entity transfer"
        subtitle="Move value between DubaiOTC ↔ IndiaOTC desks."
        help={<p>Asset must match on both legs.</p>}
      />

      <Field label="From entity">
        <EntityToggle value={srcEntity} onChange={(v) => { setSrcEntity(v); setSrc({ slug: '', asset: '' }); }} />
      </Field>

      <Field label="From account">
        <RailPicker
          value={src}
          onChange={setSrc}
          rows={srcRows}
          loading={srcTreasury.isFetching}
        />
        {src.slug && src.asset && srcRow && (
          <p className="text-meta text-tg-hint mt-1 px-1">
            Balance: <span className={`amt ${overdraw ? 'text-neg' : ''}`}>
              {fmtAmount(srcBalance, src.asset)} {displayAsset(src.asset)}
            </span>
            <> · {railName(srcRow)}</>
          </p>
        )}
      </Field>

      {/* Amount + its asset side by side, the asset read from the From
          account — the same pair Move money draws. */}
      <Field label={src.asset ? `Amount in ${displayAsset(src.asset)}` : 'Amount'}>
        <div className="flex gap-2">
          <input
            className="input flex-1"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            inputMode="decimal"
          />
          <div className="w-24 shrink-0 input bg-tg-bg/30 text-tg-hint text-center tabular text-body">
            {src.asset ? displayAsset(src.asset) : '—'}
          </div>
        </div>
      </Field>

      <Field label="To entity">
        <EntityToggle value={dstEntity} onChange={(v) => { setDstEntity(v); setDst({ slug: '', asset: '' }); }} />
      </Field>

      <Field label="To account">
        <RailPicker
          value={dst}
          onChange={setDst}
          rows={dstRows}
          loading={dstTreasury.isFetching}
        />
      </Field>

      {/* MORE — Notes, the box used less than half the time (§7 rule 8). */}
      <MoreDetails label="More">
        <Field label="Notes">
          <textarea
            className="input h-16"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Anything to remember"
          />
        </Field>
      </MoreDetails>

      {/* The refusal, in words, above the button, full width (§7 rule 11). */}
      <ErrorLine>{validation}</ErrorLine>

      <button
        className="btn-primary w-full"
        disabled={
          submit.isPending || !src.slug || !dst.slug ||
          amtN <= 0 || !!validation
        }
        onClick={() => submit.mutate({})}
      >
        {submit.isPending ? 'Posting…' : 'Post inter-entity transfer'}
      </button>

      {posted && (
        <p className="text-pos text-body">
          ✓ Moved {fmtAmount(posted.amount, posted.asset)} {displayAsset(posted.asset)} {posted.from} → {posted.to}.
          {' '}<span className="font-mono text-meta text-tg-hint">{posted.reference}</span>
        </p>
      )}
      {submit.isError && !isSentinel(submit.error) && (
        <ErrorLine className="mt-1 text-body">{errorWords(submit.error)}</ErrorLine>
      )}
      {repeat && (
        <RepeatPanel
          twin={repeat}
          pending={submit.isPending}
          onAlreadyBooked={() => { clearTwin(); nav(-1); }}
          onPostAnyway={() => submit.mutate({ confirmRepeat: true })}
        />
      )}
    </div>
  );
}

function EntityToggle({ value, onChange }: { value: Entity; onChange: (v: Entity) => void }) {
  return (
    <div className="flex gap-2">
      {(['dubaiotc', 'indiaotc'] as const).map((e) => (
        <button
          key={e}
          type="button"
          onClick={() => onChange(e)}
          className={`seg ${value === e ? 'seg-on' : ''}`}
        >
          {e === 'dubaiotc' ? 'DubaiOTC' : 'IndiaOTC'}
        </button>
      ))}
    </div>
  );
}
